import React from "react";
import { useAuth } from "../contexts/AuthContext";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import {
  UserCog,
  CreditCard,
  DollarSign,
  Cpu,
  Heart,
  Key,
  Flag,
  HelpCircle,
} from "lucide-react";
import ImpersonationSection from "../components/admin/ImpersonationSection";
import GrantCreditsSection from "../components/admin/GrantCreditsSection";
import SubscriptionTierSection from "../components/admin/SubscriptionTierSection";
import ModelPricingSection from "../components/admin/ModelPricingSection";
import SystemHealthSection from "../components/admin/SystemHealthSection";
import SubscriptionFreeAccessSection from "../components/admin/SubscriptionFreeAccessSection";
import FeatureFlagsSection from "../components/admin/FeatureFlagsSection";
import SupportRequestSection from "../components/admin/SupportRequestSection";

const AdminPage: React.FC = () => {
  const { isAdmin } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // Redirect non-admin users
  if (!isAdmin) {
    return <Navigate to="/select-project" replace />;
  }

  const sections = [
    {
      id: "support-requests",
      label: "Support Requests",
      icon: HelpCircle,
      component: SupportRequestSection,
    },
    {
      id: "subscription-tiers",
      label: "Subscription Tiers",
      icon: DollarSign,
      component: SubscriptionTierSection,
    },
    {
      id: "model-pricing",
      label: "Model Pricing",
      icon: Cpu,
      component: ModelPricingSection,
    },
    {
      id: "grant-credits",
      label: "Grant Credits",
      icon: CreditCard,
      component: GrantCreditsSection,
    },
    {
      id: "free-access",
      label: "Free Access",
      icon: Key,
      component: SubscriptionFreeAccessSection,
    },
    {
      id: "features",
      label: "Feature Flags",
      icon: Flag,
      component: FeatureFlagsSection,
    },
    {
      id: "impersonation",
      label: "Impersonation",
      icon: UserCog,
      component: ImpersonationSection,
    },
    {
      id: "system-health",
      label: "System Health",
      icon: Heart,
      component: SystemHealthSection,
    },
  ];

  // Extract current section from URL or default to first section in the list
  const currentSection =
    location.pathname.split("/admin/")[1] || sections[0].id;

  const handleSectionChange = (sectionId: string) => {
    navigate(`/admin/${sectionId}`);
  };

  const currentSectionData = sections.find(
    (section) => section.id === currentSection,
  );
  const CurrentSectionComponent =
    currentSectionData?.component || sections[0].component;

  return (
    <div className="min-h-screen bg-white dark:bg-neutral-950">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center mb-3">
            <h1 className="text-3xl font-semibold text-neutral-900 dark:text-white">
              Administration
            </h1>
          </div>
          <p className="text-neutral-600 dark:text-neutral-400 text-lg">
            Perform system administration tasks
          </p>
        </div>

        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar Navigation */}
          <div className="lg:w-64 flex-shrink-0">
            <nav className="bg-white dark:bg-neutral-800 rounded-xl border border-slate-300 dark:border-neutral-700 shadow-sm sticky top-8">
              <div className="p-2">
                {sections.map((section) => {
                  const Icon = section.icon;
                  const isActive = section.id === currentSection;

                  return (
                    <button
                      key={section.id}
                      onClick={() => handleSectionChange(section.id)}
                      className={`w-full flex items-center gap-3 px-4 py-3 text-sm font-medium rounded-lg transition-all duration-200 ${
                        isActive
                          ? "bg-brand-primary text-black shadow-sm"
                          : "text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-white hover:bg-neutral-100 dark:hover:bg-neutral-700"
                      }`}
                    >
                      <Icon size={18} />
                      {section.label}
                    </button>
                  );
                })}
              </div>
            </nav>
          </div>

          {/* Main Content Area */}
          <div className="flex-1 min-w-0">
            <div className="bg-white dark:bg-neutral-800 rounded-xl border border-slate-300 dark:border-neutral-700 shadow-sm overflow-hidden">
              <CurrentSectionComponent />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPage;
